import { ReactNode } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading } from '@digdir/designsystemet-react';
import { ClockIcon, PaperplaneIcon, PersonGroupIcon, PersonIcon } from '@navikt/aksel-icons';
import cn from 'classnames';
import { RouterUtilsImpl } from '../../utils/RouterUtils';
import { useGetTeamByTeamId } from '../../hooks/queries/useGetTeamByTeamId';
import { getInitials } from '../../utils/getInitials';
import classes from './TeamSettingsLayout.module.css';

type TeamSettingsLayoutProps = {
  children: ReactNode;
};

const routeUtils = new RouterUtilsImpl();

/**
 * Header and tabs shared by the team settings pages. Each tab is its own
 * route, so the tabs are links rather than an ARIA tablist.
 */
export const TeamSettingsLayout = ({ children }: TeamSettingsLayoutProps) => {
  const { t } = useTranslation();
  const { teamId = '' } = useParams<{ teamId: string }>();
  const { data: team } = useGetTeamByTeamId(Number(teamId));

  const tabs = [
    { route: 'TeamSetup', label: t('teamSettings.tabs.setup'), icon: PersonGroupIcon },
    { route: 'TeamAccessManagement', label: t('teamSettings.tabs.accessManagement'), icon: PersonIcon },
    { route: 'TeamIntegration', label: t('teamSettings.tabs.distributionChannels'), icon: PaperplaneIcon },
    { route: 'TeamScheduling', label: t('teamSettings.tabs.scheduling'), icon: ClockIcon },
  ] as const;

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <header className={classes.header}>
          {team && (
            <span aria-hidden="true" className={classes.avatar}>
              {getInitials(team.name)}
            </span>
          )}
          <div className={classes.heading}>
            <span className={classes.eyebrow}>{t('teamSettings.eyebrow')}</span>
            <Heading level={1} data-size="xl">
              {team?.name ?? ' '}
            </Heading>
          </div>
        </header>

        <nav aria-label={t('teamSettings.tabs.ariaLabel')}>
          <ul className={classes.tabs}>
            {tabs.map(({ route, label, icon: Icon }) => (
              <li key={route}>
                <NavLink
                  to={routeUtils.getRoute(route, teamId)}
                  className={({ isActive }) => cn(classes.tab, { [classes.activeTab]: isActive })}
                >
                  <Icon aria-hidden="true" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={classes.panel}>{children}</div>
      </div>
    </div>
  );
};
